import axios from 'axios';

const getResponseStatus = (error: unknown) =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

const getErrorCode = (error: unknown) => {
  if (!axios.isAxiosError(error)) return undefined;
  const code: unknown = error.response?.data?.error?.code;
  return typeof code === 'string' ? code : undefined;
};

const getErrorMessage = (error: unknown) => {
  if (!axios.isAxiosError(error)) return undefined;
  const message: unknown = error.response?.data?.error?.message;
  return typeof message === 'string' ? message : undefined;
};

const getDisplayErrorMessage = (error: Error) =>
  getErrorMessage(error) ?? error.message;

const FORBIDDEN_MESSAGE = '你沒有檢視此區的權限，請聯繫管理員';

const getLoadErrorMessage = (error: Error) =>
  getResponseStatus(error) === 403
    ? FORBIDDEN_MESSAGE
    : `載入失敗：${getDisplayErrorMessage(error)}`;

const getTrackingCode = (error: unknown) => {
  if (!axios.isAxiosError(error)) return undefined;
  const trackingCode: unknown = error.response?.data?.trackingCode;
  return typeof trackingCode === 'string' ? trackingCode : undefined;
};

// 檔案預覽錯誤
const VIEW_URL_FALLBACK_MESSAGE = '檔案載入失敗，請重新載入';

const VIEW_URL_CODE_MESSAGE: Record<string, string> = {
  FILE_NOT_SCANNED: '檔案尚在防毒掃描中，請稍後重新載入',
  FILE_INFECTED: '檔案未通過防毒掃描，無法預覽',
};

// view-url 的 403 是檔案不允許取用，404 是查無此檔
const VIEW_URL_STATUS_MESSAGE: Record<number, string> = {
  403: '目前無法取得此檔案，請稍後重新載入',
  404: '查無此檔案，可能已被移除',
};

const getViewUrlErrorMessage = (error: unknown) => {
  const code = getErrorCode(error);
  if (code && VIEW_URL_CODE_MESSAGE[code]) return VIEW_URL_CODE_MESSAGE[code];

  const status = getResponseStatus(error);
  return (
    (status && VIEW_URL_STATUS_MESSAGE[status]) || VIEW_URL_FALLBACK_MESSAGE
  );
};

// 審核失敗 or 審核成功
type ReviewErrorInfo = {
  title: string;
  message: string;
  // 當錯誤為文件本身的問題，不是審核人員的問題，不能重試
  mustLeave: boolean;
  trackingCode: string | undefined;
  extraMessage?: string;
};

const checkErrorField = (error: unknown) => {
  const message = getErrorMessage(error);
  if (!message) return undefined;

  const isPlateNumber = /plateNumber|plate\s*number/i.test(message);
  const isNationalId = /nationalId|national\s*id/i.test(message);

  if (isPlateNumber) return '車牌';
  if (isNationalId) return '身分證';
  return undefined;
};

const buildReviewErrorInfo = (error: unknown): ReviewErrorInfo => {
  const base = { trackingCode: getTrackingCode(error) };
  const errorField = checkErrorField(error);

  switch (getResponseStatus(error)) {
    case 400:
      return {
        ...base,
        title: '資料格式有誤',
        message: `送出的內容不符合規格，請檢查${errorField ? `「${errorField}」` : ''}欄位後重新送出。`,
        extraMessage: getErrorMessage(error),
        mustLeave: false,
      };
    case 401:
      return {
        ...base,
        title: '登入狀態已失效',
        message: '請重新登入後再回到這裡送出審核結果。',
        mustLeave: false,
      };
    case 403:
      return {
        ...base,
        title: '沒有權限',
        message: '你的帳號沒有執行此審核操作的權限，請聯絡管理員。',
        mustLeave: false,
      };
    case 404:
      return {
        ...base,
        title: '查無此文件',
        message: '這份文件可能已被刪除，請回列表重新選擇。',
        mustLeave: true,
      };
    case 409:
      return {
        ...base,
        title: '文件狀態已變更',
        message:
          '這份文件已被其他人審核，或狀態已經改變，無法再次審核。請回列表確認最新狀態。',
        mustLeave: true,
      };
    default:
      return {
        ...base,
        title: '送出失敗',
        message: '請確認網路狀態後重新送出；若持續失敗請聯絡工程團隊。',
        mustLeave: false,
      };
  }
};

export {
  VIEW_URL_FALLBACK_MESSAGE,
  buildReviewErrorInfo,
  getDisplayErrorMessage,
  getLoadErrorMessage,
  getErrorCode,
  getErrorMessage,
  getResponseStatus,
  getTrackingCode,
  getViewUrlErrorMessage,
};
export type { ReviewErrorInfo };
